/**
 * Markdown ↔ HTML bridge for email body editing.
 *
 * MIRRORS `_pipeline/email_common.py::plain_to_html`. The model emits
 * markdown-flavored plain text ("**bold**", "- bullets"); the SEND path on the
 * backend converts it to HTML before posting to Gmail/Outlook. This frontend
 * helper exists so the IN-EDITOR preview matches what the recipient will see —
 * otherwise the user edits raw asterisks while the recipient sees real bold.
 *
 * Drift between the two implementations breaks that promise. Conformance is
 * pinned by `_backend/tests/email_markdown_spec.json`. The Python side asserts
 * via `_backend/tests/test_email_markdown.py`. Update both files together when
 * changing the supported markdown set.
 *
 * Supported markdown:
 *   **bold**, *italic*, "- " or "• " bullet lines, "1." / "1)" numbered
 *   lines, newlines → <br>.
 *
 * is_html() heuristic mirrors `_pipeline/email_common.is_html` so the
 * EmailBodyEditor's "is this already HTML?" decision matches the backend's
 * decision on incoming send bodies.
 */

const HTML_TAG_TEST = /<(div|p|br|ul|ol|li|b|i|strong|em|a |span |table)\b/i;

export function isHtml(text: string): boolean {
  return HTML_TAG_TEST.test((text || '').trim());
}

export function plainToHtml(text: string): string {
  if (!text) return '';
  let escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Bold first — wraps "**word**" in <b>. Greedy enough that subsequent
  // italic regex (negative-lookaround on *) doesn't double-wrap the * pairs.
  escaped = escaped.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
  // Italic — single * not preceded/followed by another *
  escaped = escaped.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<i>$1</i>');

  const lines = escaped.split('\n');
  const out: string[] = [];
  let inList: 'ul' | 'ol' | null = null;
  for (const line of lines) {
    const stripped = line.trim();
    const isBullet = stripped.startsWith('- ') || stripped.startsWith('• ');
    const numMatch = !isBullet ? stripped.match(/^(\d+)[.)]\s+(.*)/) : null;

    if (isBullet) {
      if (inList !== 'ul') {
        if (inList) out.push(`</${inList}>`);
        out.push("<ul style='margin:4px 0;padding-left:20px'>");
        inList = 'ul';
      }
      out.push(`<li>${stripped.slice(2)}</li>`);
    } else if (numMatch) {
      if (inList !== 'ol') {
        if (inList) out.push(`</${inList}>`);
        out.push("<ol style='margin:4px 0;padding-left:20px'>");
        inList = 'ol';
      }
      out.push(`<li>${numMatch[2]}</li>`);
    } else {
      if (inList) {
        out.push(`</${inList}>`);
        inList = null;
      }
      out.push(`${line}<br>`);
    }
  }
  if (inList) out.push(`</${inList}>`);
  return out.join('\n');
}

export function htmlToPlain(html: string): string {
  let text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n');
  return text.trim();
}
